/**
 * Royalty statement status + money helpers.
 *
 * Bulk-create returns statements as `pending`; a background task then moves
 * each one `pending` → `generating` → `draft` (or `error`), filling in the
 * PDF and money fields along the way. Ledger mutations can also flip a
 * `draft` back to `pending` to regenerate it.
 */
import { formatCurrencyFromString } from "@/lib/helpers";
import type { RoyaltyStatementStatus } from "@/types";

/**
 * Statuses still moving through the generation pipeline. `draft` and `error`
 * are terminal (an errored generation must not keep polling alive).
 */
export function isRoyaltyStatementGenerating(status: RoyaltyStatementStatus): boolean {
  return status === "pending" || status === "generating";
}

/**
 * Money display for statement value / balance. While generating, the backend
 * still holds the default 0 — indistinguishable from a real zero by value
 * alone — so it is masked with "-".
 */
export function formatRoyaltyStatementAmount(
  amount: string,
  currency: string,
  locale: string,
  status: RoyaltyStatementStatus,
): string {
  if (isRoyaltyStatementGenerating(status)) return "-";
  return formatCurrencyFromString(amount, currency, locale);
}
